import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { useState, useRef, MouseEvent, ElementRef } from 'react';
import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import MegaNavigationBody from 'components/Generic/Header/MegaNavigation/MegaNavigationBody';
import useWindowScrollToggle from '../../../../hooks/useToggleWindowScroll';
import { NavItem } from './MegaNavigation.types';
import styles from './megaNavigationMenuItem.module.scss';

type MegaNavigationMenuItemProps = {
  active: boolean;
  category: NavItem;
  onSetActiveCategory: (data: NavItem | null) => void;
};

const MegaNavigationMenuItem = (props: MegaNavigationMenuItemProps): JSX.Element => {
  const [lastClickedMenuItem, setLastClickedMenuItem] = useState<ElementRef<'button'> | null>(null);
  const overlay = useRef(null);
  const { toggleWindowScroll } = useWindowScrollToggle();

  const closeMegaNavBody = () => {
    props.onSetActiveCategory(null);
  };

  const onMegaNavButtonClick = (event: MouseEvent<HTMLButtonElement>, category: NavItem) => {
    if (props.active) {
      closeMegaNavBody();
    } else {
      setLastClickedMenuItem(event.currentTarget);
      /*
       * NOTE: `setTimeout` is necessary because the code inside
       * of it has to be called later than `onMegaNavBodyFocusOut`.
       */
      setTimeout(() => {
        event.preventDefault();
        props.onSetActiveCategory(category);
        toggleWindowScroll(true);
      }, 10);
    }
  };
  const restoreFocus = (): void => {
    lastClickedMenuItem?.focus();
  };

  return (
    <li className={`px-2 ${styles['meganavigation-menu-item']}`} tabIndex={-1}>
      {props.category.hasLinks && (
        <>
          <div
            role="button"
            ref={overlay}
            aria-label="overlay to close mega navigation"
            className={`${styles['meganavigation-menu-item-overlay']} d-none d-xl-block ${
              props.active ? styles.active : ''
            }`}
            tabIndex={-1}
            onClick={() => {
              closeMegaNavBody();
              restoreFocus();
            }}
          />
          <button
            className={`btn btn-naked header-link ${styles['meganav__button']} ${
              props.active ? styles.active : ''
            }`}
            aria-expanded={props.active ? 'true' : 'false'}
            onClick={(event) => onMegaNavButtonClick(event, props.category)}
          >
            <Text
              tag="span"
              className={styles['meganav__button__text']}
              field={props.category.fields.title}
            />
            <i className={`bi bi-chevron-down ${styles['meganav__button__icon']}`} aria-hidden />
          </button>
          {props.active && (
            <MegaNavigationBody
              category={props.category}
              active={props.active}
              closeMegaNavBody={() => closeMegaNavBody()}
            />
          )}
        </>
      )}
      {!props.category.hasLinks && props.category.directLink && (
        <ScLink
          field={props.category.directLink}
          onClick={closeMegaNavBody}
          replaceWithSlot={true}
          showExternalIcon={true}
          className={`btn btn-naked header-link ${styles['meganav__button']}`}
        >
          <span className={styles['meganav__button__text']}>
            {props.category.directLink.value.text}
          </span>
        </ScLink>
      )}
    </li>
  );
};

export default MegaNavigationMenuItem;
